import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/currency";

const EVENT_LABELS: Record<string, { label: string; color: string }> = {
  send:      { label: "Sent",      color: "bg-blue-100 text-blue-800" },
  delivery:  { label: "Delivered", color: "bg-green-100 text-green-800" },
  open:      { label: "Opened",    color: "bg-purple-100 text-purple-800" },
  click:     { label: "Clicked",   color: "bg-indigo-100 text-indigo-800" },
  bounce:    { label: "Bounced",   color: "bg-red-100 text-red-800" },
  complaint: { label: "Junk/Spam", color: "bg-orange-100 text-orange-800" },
  reject:    { label: "Rejected",  color: "bg-gray-100 text-gray-800" },
};

const TEMPLATE_LABELS: Record<string, string> = {
  trial_expiry_3days:       "Trial — 3 Days Warning",
  trial_expiry_today:       "Trial — Expiry Day",
  trial_expiry_3days_after: "Trial — 3 Days After",
};

export default function EmailStatsTab() {
  const [eventsPage, setEventsPage] = useState(1);
  const [templatePage, setTemplatePage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date'|'recipient'|'subject'>('date');
  const [sortDir, setSortDir] = useState<'desc'|'asc'>('desc');
  const { data: stats, isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/triggers/stats", eventsPage, sortBy, sortDir, templatePage, selectedTemplate],
    queryFn: () => apiRequest("GET", `/api/email-mgmt/triggers/stats?eventsPage=${eventsPage}&sortBy=${sortBy}&sortDir=${sortDir}&templatePage=${templatePage}${selectedTemplate ? `&templateFilter=${encodeURIComponent(selectedTemplate)}` : ''}`) as unknown as Promise<any>,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  if (isLoading) return <div className="text-muted-foreground text-sm">Loading stats...</div>;
  if (!stats) return <div className="text-muted-foreground text-sm">No data yet.</div>;

  const deliveryRate = stats.eventCounts?.find((e: any) => e._id === "delivery")?.count || 0;
  const openRate = stats.eventCounts?.find((e: any) => e._id === "open")?.count || 0;
  const bounceCount = stats.eventCounts?.find((e: any) => e._id === "bounce")?.count || 0;
  const complaintCount = stats.eventCounts?.find((e: any) => e._id === "complaint")?.count || 0;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{stats.totalSent}</div>
            <div className="text-sm text-muted-foreground">Total Sent</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{deliveryRate}</div>
            <div className="text-sm text-muted-foreground">Delivered</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">{openRate}</div>
            <div className="text-sm text-muted-foreground">Opened</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className={`text-2xl font-bold ${bounceCount > 0 ? "text-red-600" : ""}`}>{bounceCount}</div>
            <div className="text-sm text-muted-foreground">Bounced</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Event breakdown */}
        <Card>
          <CardHeader><CardTitle className="text-base">Event Breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {stats.eventCounts?.length === 0 && (
              <p className="text-sm text-muted-foreground">No events tracked yet. Events appear after emails are sent with SNS-CONFIG.</p>
            )}
            {stats.eventCounts?.map((e: any) => {
              const meta = EVENT_LABELS[e._id] || { label: e._id, color: "bg-gray-100 text-gray-800" };
              return (
                <div key={e._id} className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>{meta.label}</span>
                  <span className="font-semibold">{e.count}</span>
                </div>
              );
            })}
            {complaintCount > 0 && (
              <p className="text-xs text-orange-600 mt-2">⚠️ {complaintCount} complaint(s) — consider reviewing your email content</p>
            )}
          </CardContent>
        </Card>

        {/* Per-template breakdown */}
        <Card>
          <CardHeader><CardTitle className="text-base">Sends by Template</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {stats.byTemplate?.length === 0 && (
              <p className="text-sm text-muted-foreground">No emails sent yet.</p>
            )}
            {stats.byTemplate?.map((t: any) => (
              <div key={t._id} onClick={() => { setSelectedTemplate(s => s === t._id ? null : t._id); setEventsPage(1); }}
                className={`space-y-1 border-b pb-2 last:border-0 cursor-pointer rounded px-1 -mx-1 transition-colors ${selectedTemplate === t._id ? "bg-primary/5 ring-1 ring-primary/20" : "hover:bg-muted/50"}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium flex items-center gap-1">
                    {TEMPLATE_LABELS[t._id] || t._id}
                    {t.isBlast && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 rounded">blast</span>}
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary">{t.count} sent</Badge>
                    <div className="text-xs text-muted-foreground">Last: {t.lastSent ? new Date(t.lastSent).toLocaleDateString() : "—"}</div>
                  </div>
                </div>
                {t.events && (
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {[['delivery','Delivered','text-green-700 bg-green-50'],['open','Opened','text-purple-700 bg-purple-50'],['click','Clicked','text-indigo-700 bg-indigo-50'],['bounce','Bounced','text-red-700 bg-red-50'],['complaint','Junk','text-orange-700 bg-orange-50']].map(([key, label, cls]) =>
                      t.events[key] ? <span key={key} className={`px-1.5 py-0.5 rounded ${cls}`}>{label}: {t.events[key]}</span> : null
                    )}
                  </div>
                )}
              </div>
            ))}
            {stats.totalTemplates > stats.templateLimit && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <button onClick={() => setTemplatePage(p => Math.max(1, p - 1))} disabled={templatePage === 1}
                  className="text-xs px-3 py-1 border rounded disabled:opacity-40 hover:bg-muted">← Prev</button>
                <span className="text-xs text-muted-foreground">Page {templatePage} of {Math.ceil(stats.totalTemplates / stats.templateLimit)}</span>
                <button onClick={() => setTemplatePage(p => p + 1)} disabled={templatePage >= Math.ceil(stats.totalTemplates / stats.templateLimit)}
                  className="text-xs px-3 py-1 border rounded disabled:opacity-40 hover:bg-muted">Next →</button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent events */}
      {(stats.recentEvents?.length > 0 || stats.totalEvents > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">
                Recent Events
                {selectedTemplate && (
                  <span className="ml-2 text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    {selectedTemplate}
                    <button onClick={() => { setSelectedTemplate(null); setEventsPage(1); }} className="ml-1 hover:text-destructive">×</button>
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{stats.totalEvents} total</span>
                <select value={sortBy} onChange={e => { setSortBy(e.target.value as any); setEventsPage(1); }}
                  className="text-xs border rounded px-2 py-1 bg-background">
                  <option value="date">Sort: Date</option>
                  <option value="recipient">Sort: Recipient</option>
                  <option value="subject">Sort: Subject</option>
                </select>
                <button onClick={() => { setSortDir(d => d === 'desc' ? 'asc' : 'desc'); setEventsPage(1); }}
                  className="text-xs border rounded px-2 py-1 bg-background hover:bg-muted">
                  {sortDir === 'desc' ? '↓' : '↑'}
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(stats.recentEvents || []).map((e: any) => {
                const meta = EVENT_LABELS[e.eventType] || { label: e.eventType, color: "bg-gray-100 text-gray-800" };
                const subject = e.subject || e.raw?.mail?.commonHeaders?.subject || e.raw?.mail?.subject || "";
                const recipient = e.recipients?.[0] || e.raw?.mail?.destination?.[0] || "—";
                return (
                  <div key={e._id} className="flex items-center gap-3 text-sm border-b pb-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${meta.color}`}>{meta.label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-muted-foreground">{recipient}</div>
                      {subject && <div className="truncate text-xs text-muted-foreground/70">{subject}</div>}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {new Date(e.timestamp).toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
            {stats.totalEvents > stats.eventsLimit && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <button onClick={() => setEventsPage(p => Math.max(1, p - 1))} disabled={eventsPage === 1}
                  className="text-xs px-3 py-1 border rounded disabled:opacity-40 hover:bg-muted">← Prev</button>
                <span className="text-xs text-muted-foreground">Page {eventsPage} of {Math.ceil(stats.totalEvents / stats.eventsLimit)}</span>
                <button onClick={() => setEventsPage(p => p + 1)} disabled={eventsPage >= Math.ceil(stats.totalEvents / stats.eventsLimit)}
                  className="text-xs px-3 py-1 border rounded disabled:opacity-40 hover:bg-muted">Next →</button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
